/**
 * Files on a message: the chips in the composer before sending, and the same
 * files under the person's message after. One component so the two agree,
 * drawn from the prototype's attachment chip (30px, sunken, icon, name, ×).
 * Images show themselves; everything else shows a Phosphor glyph for its type.
 */

import { fileUrl, type Attachment } from '../api';
import { Ph } from './ui';

export function fileIcon(mime: string): string {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'file-pdf';
  if (mime.startsWith('video/')) return 'film-strip';
  if (mime.startsWith('audio/')) return 'file-audio';
  if (mime.startsWith('text/') || mime === 'application/json') return 'file-text';
  return 'file';
}

export function AttachmentChip({
  name,
  mime,
  fileId,
  state = 'ready',
  error,
  onRemove
}: {
  name: string;
  mime: string;
  /** Set once uploaded; images then show a thumbnail from it. */
  fileId: string | null;
  state?: 'uploading' | 'ready' | 'failed';
  error?: string;
  onRemove?: () => void;
}) {
  const failed = state === 'failed';
  return (
    <span
      title={failed ? error : name}
      style={{
        height: 30,
        maxWidth: 240,
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: onRemove ? '0 6px 0 10px' : '0 10px',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--app-panel-sunken)',
        fontSize: 12.5,
        color: failed ? 'var(--danger-600)' : 'var(--text-body)'
      }}
    >
      {state === 'uploading' ? (
        <Ph name="circle-notch" size={14} className="spin" color="var(--text-muted)" />
      ) : failed ? (
        <Ph name="warning-circle" size={14} color="var(--danger-500)" />
      ) : mime.startsWith('image/') && fileId ? (
        <img
          src={fileUrl(fileId)}
          alt=""
          style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover', flex: 'none' }}
        />
      ) : (
        <Ph name={fileIcon(mime)} size={14} color="var(--text-muted)" />
      )}
      <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
      {onRemove ? (
        <button
          type="button"
          className="bare hov-strong"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
          style={{
            width: 20,
            height: 20,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 4,
            color: 'var(--text-faint)'
          }}
        >
          <Ph name="x" size={11} />
        </button>
      ) : null}
    </span>
  );
}

/** The files a sent message carried: images as thumbnails, the rest as chips
 *  that open the file. */
export function SentAttachments({ attachments, align = 'end' }: { attachments: Attachment[]; align?: 'start' | 'end' }) {
  if (!attachments.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: align === 'end' ? 'flex-end' : 'flex-start', gap: 6 }}>
      {attachments.map((a) =>
        a.mime.startsWith('image/') ? (
          <a key={a.fileId} href={fileUrl(a.fileId)} target="_blank" rel="noopener noreferrer" title={a.name}>
            <img
              src={fileUrl(a.fileId)}
              alt={a.name}
              style={{
                display: 'block',
                maxWidth: 240,
                maxHeight: 180,
                borderRadius: 'var(--radius-app-card)',
                objectFit: 'cover',
                background: 'var(--app-panel-sunken)'
              }}
            />
          </a>
        ) : (
          <a key={a.fileId} href={fileUrl(a.fileId)} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
            <AttachmentChip name={a.name} mime={a.mime} fileId={a.fileId} />
          </a>
        )
      )}
    </div>
  );
}
